import React from 'react';

import {
	Check,
	ChevronDown,
	ChevronUp,
	Clock,
	RefreshCw,
	XCircle,
} from 'lucide-react';

import ProgressIcon from '../../assets/icons/progress.svg';
import { formatDuration } from '../../utils/utils';
import Button from '../common/Button';
import ProcessingLogs from '../ProcessingLogs';
import { hasCreditSettlementFailure } from '../processingStatus';

import type { ProcessingLogs as ProcessingLogsData } from '../../api';
import type { LucideIcon } from 'lucide-react';

// Friendly, frontend-only processing stages. We intentionally do NOT surface
// the underlying platform run phases / percentages here — they're internal and
// not user-friendly. The dispatch is async (see platform_broker.submit_async),
// so the UI just reassures the user while the row stays `processing`; the page
// keeps polling and flips to the completed view when done.
const FRIENDLY_STAGES = [
	'Processing started…',
	'Processing…',
	'Processing in the background…',
];
const STAGE_WIDTHS = [25, 55, 80];
const STAGE_ADVANCE_MS = 4000;

// Customer-facing copy only. The raw failure reason (which can carry upstream
// error text) stays in the processing log below, never in this banner.
export const FAILED_TITLE = 'Statement processing failed';
export const FAILED_MESSAGE =
	"We couldn't finish analyzing this statement. Check the processing log below, then run the analysis again.";
export const COMPLETED_TITLE = 'Statement Processing Completed';

interface Presentation {
	Icon: LucideIcon;
	title: string;
	message?: string;
	header: string;
	chip: string;
	iconColor: string;
	titleColor: string;
}

const NEUTRAL: Omit<Presentation, 'Icon' | 'title'> = {
	header: 'bg-white border-neutral-200',
	chip: 'bg-neutral-100',
	iconColor: 'text-neutral-500',
	titleColor: 'text-neutral-900',
};

// Status → presentation, as data. Adding a backend status is a row here, not
// a fifth branch; anything not listed renders as UNKNOWN. `processing` is a
// different layout (animated stages), not a variant, and keeps its own render.
const PRESENTATION: Record<string, Presentation> = {
	failed: {
		Icon: XCircle,
		title: FAILED_TITLE,
		message: FAILED_MESSAGE,
		header: 'bg-error-50 border-error-200',
		chip: 'bg-error-100',
		iconColor: 'text-error-600',
		titleColor: 'text-error-800',
	},
	completed: {
		Icon: Check,
		title: COMPLETED_TITLE,
		header: 'bg-white border-neutral-200',
		chip: 'bg-violet-100',
		iconColor: 'text-indigo-700',
		titleColor: 'text-neutral-900',
	},
	uploaded: {
		...NEUTRAL,
		Icon: Clock,
		title: 'Statement uploaded — processing has not started',
	},
	cancelled: {
		...NEUTRAL,
		Icon: Clock,
		title: 'Statement processing was cancelled',
	},
};
const UNKNOWN: Presentation = {
	...NEUTRAL,
	Icon: Clock,
	title: 'Statement processing status is unavailable',
};

interface ProcessingStepProps {
	isCollapsed: boolean;
	onToggle: () => void;
	/** Backend `current_status`; anything outside the known set renders neutrally. */
	status: string;
	logsData?: ProcessingLogsData;
	onRetry: () => void;
	/** Opens the rerun flow. Rendered as the recovery action when processing failed. */
	onRerun?: () => void;
	isRerunning?: boolean;
}

const ProcessingStep: React.FC<ProcessingStepProps> = ({
	isCollapsed,
	onToggle,
	status,
	logsData,
	onRetry,
	onRerun,
	isRerunning = false,
}) => {
	// Advance through the friendly stages on a timer while processing. This is
	// cosmetic reassurance, not real platform progress.
	const [stageIdx, setStageIdx] = React.useState(0);
	React.useEffect(() => {
		if (status !== 'processing') {
			return;
		}
		setStageIdx(0);
		const timer = setInterval(() => {
			setStageIdx((i) => (i < FRIENDLY_STAGES.length - 1 ? i + 1 : i));
		}, STAGE_ADVANCE_MS);
		return () => clearInterval(timer);
	}, [status]);

	const totalDuration = logsData?.logs.length
		? formatDuration(
				logsData.logs.reduce(
					(acc, log) => acc + (log.duration_seconds ?? 0),
					0,
				),
			)
		: null;

	const toggleButton = (
		<button
			className="p-1 hover:bg-neutral-100 rounded-sm"
			aria-label={isCollapsed ? 'Show processing log' : 'Hide processing log'}
		>
			{isCollapsed ? (
				<ChevronDown className="w-4 h-4 text-neutral-500" />
			) : (
				<ChevronUp className="w-4 h-4 text-neutral-500" />
			)}
		</button>
	);

	const logs = !isCollapsed && (
		<div>
			<ProcessingLogs logsData={logsData} onRetry={onRetry} />
		</div>
	);

	if (status === 'processing') {
		/* Processing State Layout — friendly, frontend-only messaging */
		return (
			<div className="transition-all duration-500">
				<div className="w-full bg-white border-b border-gray-200">
					<div className="w-full px-6 py-5">
						<div className="flex items-center gap-3">
							<div className="w-9 h-9 bg-violet-100 rounded-full flex items-center justify-center animate-pulse">
								<ProgressIcon className="w-5 h-5 text-indigo-700" />
							</div>
							<div className="flex flex-col">
								<span className="text-base font-medium text-neutral-900 font-primary">
									{FRIENDLY_STAGES[stageIdx]}
								</span>
								<span className="text-sm text-neutral-500 font-primary">
									We&rsquo;re analyzing your statement. You can keep working
									&mdash; this page updates automatically when it&rsquo;s ready.
								</span>
							</div>
						</div>
					</div>

					{/* Friendly progress bar (not platform progress) */}
					<div className="w-full h-1 bg-gray-200">
						<div
							className="h-full bg-purple-600 transition-all duration-700 ease-out"
							style={{ width: `${STAGE_WIDTHS[stageIdx]}%` }}
						/>
					</div>
				</div>
			</div>
		);
	}

	const view = PRESENTATION[status] ?? UNKNOWN;
	// Credit-settlement failures keep their own legacy action inside the log;
	// showing the rerun button too would offer two competing recoveries.
	const showRerun =
		status === 'failed' &&
		Boolean(onRerun) &&
		!hasCreditSettlementFailure(logsData);
	const showDuration = status === 'completed' && totalDuration !== null;

	// Collapsing hides the log (it is simply not rendered); the banner itself
	// is never clipped, so a two-line failed banner stays readable.
	return (
		<div className="transition-all duration-500">
			<div
				className={`w-full px-6 py-4 border-b cursor-pointer ${view.header}`}
				onClick={onToggle}
			>
				<div className="w-full flex justify-between items-center gap-4">
					<div className="flex items-center gap-3 min-w-0">
						<div
							className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${view.chip}`}
						>
							<view.Icon className={`w-5 h-5 ${view.iconColor}`} />
						</div>
						<div className="flex flex-col min-w-0">
							<div className="flex items-center gap-2">
								<span
									className={`text-base font-medium font-primary ${view.titleColor}`}
								>
									{view.title}
								</span>
								{toggleButton}
							</div>
							{view.message && (
								<span className="text-sm text-error-700 font-primary">
									{view.message}
								</span>
							)}
						</div>
					</div>
					{showRerun && (
						<Button
							variant="outline"
							size="sm"
							icon={RefreshCw}
							loading={isRerunning}
							onClick={(event) => {
								event.stopPropagation();
								onRerun?.();
							}}
						>
							Retry analysis
						</Button>
					)}
					{showDuration && (
						<div className="flex items-center gap-2 text-sm text-success-500 font-primary">
							<Clock className="w-4 h-4" />
							<span className="font-medium">
								Statement processed in just {totalDuration}
							</span>
						</div>
					)}
				</div>
			</div>
			{logsData && logs}
		</div>
	);
};

export default ProcessingStep;
